import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './SignUpModal.css';
import { useNavigate } from 'react-router-dom';
// Add Supabase import
import supabase from "../supabaseClient";

const SignUpModal = ({ open, onClose, onSignUp, error, onSwitchToSignIn }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: credentials, 2: verify email info
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Check password matching when either password field changes
    if (name === 'password' || name === 'confirmPassword') {
      if (name === 'password') {
        setPasswordsMatch(value === formData.confirmPassword || formData.confirmPassword === '');
      } else {
        setPasswordsMatch(value === formData.password);
      }
    }
  };
  
  const nextStep = () => setStep(prev => prev + 1);
  
  // Add this function after handleSubmit but before handleSocialLoginStart
  
  const registerUser = async (userData) => {
    try {
      setLoading(true);
      setLocalError('');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` }
      });
  
      if (authError) throw authError;
      // On success, move to verify email step
      setLoading(false);
      return { success: true, user: authData.user };
    } catch (error) {
      console.error("Registration error:", error);
      setLoading(false);
      setLocalError(error.message || "Failed to register. Please try again.");
      return { success: false, error };
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const result = await registerUser(formData);
    
    if (result.success) {
      // Step 2: show verify email instructions
      setStep(2);
    }
  };
  
  // Add social login handlers
  const handleSocialLoginStart = (provider) => {
    setLoading(provider); // Change to store which provider is loading
    setLocalError('');
  };

  const handleSocialLoginError = (errorMsg) => {
    setLocalError(errorMsg || 'Social login failed');
    setLoading(false);
  };
  
  // Update these social login handler functions
  const handleGoogleSignUp = async (e) => {
    e.preventDefault();
    handleSocialLoginStart('google'); // Pass provider name
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({ // Line 280 context
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
  
      if (error) {
        // Use the error variable for logging or displaying a message
        console.error('Google sign up OAuth error:', error.message);
        handleSocialLoginError(`Google sign up failed: ${error.message}`);
        return; // Stop execution if there's an error
      }
  
      // Use the data variable (optional, example shown)
      if (data) {
         console.log('Google OAuth initiated. Redirect URL:', data.url);
         // Potentially store data.url or other info if needed
      }
  
      // The redirect will happen automatically by Supabase if no error
      console.log('Google sign up initiated successfully');
    } catch (error) {
      // Catch any other unexpected errors during the process
      console.error('Google sign up unexpected error:', error);
      handleSocialLoginError(error.message || 'An unexpected error occurred during Google sign up.');
    }
  };

  const handleFacebookSignUp = async (e) => {
    e.preventDefault();
    handleSocialLoginStart('facebook'); // Pass provider name
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
  
      if (error) {
        // Use the error variable
        console.error('Facebook sign up OAuth error:', error.message);
        handleSocialLoginError(`Facebook sign up failed: ${error.message}`);
        return;
      }
      
      // Use the data variable (optional)
      if (data) {
         console.log('Facebook OAuth initiated. Redirect URL:', data.url);
      }
  
      // The redirect will happen automatically by Supabase
      console.log('Facebook sign up initiated successfully');
    } catch (error) {
      console.error('Facebook sign up unexpected error:', error);
      handleSocialLoginError(error.message || 'An unexpected error occurred during Facebook sign up.');
    }
  };

  const handleGithubSignUp = async (e) => {
    e.preventDefault();
    handleSocialLoginStart('github'); // Pass provider name
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
  
      if (error) {
        // Use the error variable
        console.error('GitHub sign up OAuth error:', error.message);
        handleSocialLoginError(`GitHub sign up failed: ${error.message}`);
        return;
      }
      
      // Use the data variable (optional)
      if (data) {
         console.log('GitHub OAuth initiated. Redirect URL:', data.url);
      }
  
      // The redirect will happen automatically by Supabase
      console.log('GitHub sign up initiated successfully');
    } catch (error) {
      console.error('GitHub sign up unexpected error:', error);
      handleSocialLoginError(error.message || 'An unexpected error occurred during GitHub sign up.');
    }
  };

  // Validation for credentials step only
  const validateStep1 = () => (
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    passwordsMatch
  );
  
  if (!open) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-content signup-modal">
        <button className="close-button" onClick={onClose}>&times;</button>
        <h2>Sign Up</h2>

        {(error || localError) && <div className="error-message">{error || localError}</div>}

        {/* Step 1: Account credentials */}
        {step === 1 && (
          <form>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Create Password</label>
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-field">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowConfirm(v => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {!passwordsMatch && (
                <small className="error-text">Passwords do not match</small>
              )}
            </div>
            
            <div className="step-buttons">
              <button 
                type="button" 
                className="continue-button" 
                onClick={handleSubmit}
                disabled={!validateStep1() || loading}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </div>
            
            {/* Social login remains optional */}
            <div className="social-login-container">
              <div className="social-login-divider">Or sign up with</div>
              <div className="social-buttons">
                <button 
                  type="button"
                  className="social-button google"
                  onClick={handleGoogleSignUp}
                  disabled={loading} // Keep this as is
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#4285F4">
                    <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
                  </svg>
                  {loading === 'google' ? 'Loading...' : 'Google'} {/* Only show loading for this provider */}
                </button>
                <button 
                  type="button"
                  className="social-button facebook"
                  onClick={handleFacebookSignUp}
                  disabled={loading} // Keep this as is
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#3b5998">
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                  </svg>
                  {loading === 'facebook' ? 'Loading...' : 'Facebook'} {/* Only show loading for this provider */}
                </button>
                <button 
                  type="button"
                  className="social-button github"
                  onClick={handleGithubSignUp}
                  disabled={loading} // Keep this as is
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="var(--text-light)">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  {loading === 'github' ? 'Loading...' : 'GitHub'} {/* Only show loading for this provider */}
                </button>
              </div>
            </div>
            
            <div className="modal-footer">
              <p>Already have an account? <button type="button" className="text-button" onClick={onSwitchToSignIn}>Sign In</button></p>
            </div>
          </form>
        )}
        {/* Step 2: Verify Email instructions */}
        {step === 2 && (
          <div className="signup-confirmation">
            <div className="success-icon">✓</div>
            <h3>Account Created!</h3>
            <p>We sent a verification link to <strong>{formData.email}</strong>.</p>
            <p>Please verify your email to continue. After verification, sign in and you'll be guided to complete your profile.</p>
            <div className="step-buttons">
              <button type="button" className="continue-button" onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignUpModal;